import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import PhoneInputFormItem from '@controls/phone-input-form-item/phone-input-form-item';

import { Input, InputNumber, Space, Button, Form, Select, Tooltip, notification } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';

import FormHeader from '@controls/form-header/form-header';
import Tag from '@components/tag/tag';

import { exception } from '@extensions/notification';

import { userLoaded } from '@store/actions';
import { useAppDispatch, useAppSelector } from '@store/hooks';

import { serverFetch } from '@src/server';

import { IUserSession } from '@src/entities/user-session';
import { IConsignee } from '@entities/consignee';
import { ICity } from '@entities/city';
import { IUserFilter } from '@entities/user-filter';
import { IUser } from '@entities/user';
import { ICountry } from '@entities/country';

import { UserType } from '@enums/user-type';
import { Permission, hasPermission } from '@src/enums/permission';

const Consignee = () => {
    const { id } = useParams();

    const navigate = useNavigate();
    const [api, contextHolder] = notification.useNotification();

    const { TextArea } = Input;

    const [form] = Form.useForm();

    const d = useAppDispatch();
    const userSession = useAppSelector<IUserSession>((s) => s.userSession);

    const [entity, setEntity] = useState<IConsignee>({
        id: undefined,
        userId: undefined,
        cityId: undefined,
        isArchived: false,
        type: undefined,
    });

    const [loading, setLoading] = useState<boolean>(false);
    const [cities, setCities] = useState<Array<ICity>>([]);
    const [users, setUsers] = useState<Array<IUser>>([]);
    const [isViewOnly] = useState<boolean>(!hasPermission(userSession.permissions, Permission.ManageConsignees));

    useEffect(() => {
        let cleanup = false;

        const fetchData = async () => {
            setLoading(true);

            const userFilter: IUserFilter = { isActive: true, isArchived: false, types: [UserType.MainClient] };

            const promises = [
                await serverFetch('cities', { method: 'GET' })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения городов', ex, () => d(userLoaded(undefined)));
                    }),

                await serverFetch('users', { method: 'GET', queryParams: userFilter })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения пользователей', ex, () => d(userLoaded(undefined)));
                    }),

                await serverFetch('countries', { method: 'GET', queryParams: { withTransportOnly: true } })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения стран', ex, () => d(userLoaded(undefined)));
                    }),
            ];

            if (id) {
                promises.push(
                    await serverFetch(`consignees/${id}`, { method: 'GET' })
                        .then((data) => {
                            return data;
                        })
                        .catch((ex) => {
                            exception(api, 'Ошибка получения грузополучателя', ex, () => d(userLoaded(undefined)));
                        }),
                );
            }

            Promise.all([promises]).then((result) => {
                if (cleanup) return;

                setCities(result[0][0]);
                var serverUsers: Array<IUser> = result[0][1];

                setUsers(serverUsers.filter(u=>!u.syncDataType));

                const consignee = result[0][3] as IConsignee;
                if (consignee) {
                    if (consignee.tag) {
                        try {
                            consignee.tags = JSON.parse(consignee.tag);
                        } catch {
                            consignee.tags = [];
                            consignee.tags.push(consignee.tag);
                        }
                    }

                    setEntity({ ...consignee });
                } else {
                    setEntity({ ...entity });
                }

                setLoading(false);
            });
        };

        fetchData();
        return () => {
            cleanup = true;
        };
    }, []);

    const onSave = () => {
        setLoading(true);

        entity.tag = JSON.stringify(entity.tags);

        serverFetch(`consignees`, { method: id ? 'PUT' : 'POST', bodyData: entity })
            .then(() => {
                setLoading(false);
                navigate(-1);
            })
            .catch((ex) => {
                setLoading(false);
                exception(api, 'Ошибка сохранения грузополучателя', ex, () => d(userLoaded(undefined)));
            });
    };

    const onRestore = () => {
        serverFetch(`consignees/${id}/restore`, { method: 'POST' })
            .then(() => {
                setLoading(false);
                navigate(-1);
            })
            .catch((ex) => {
                setLoading(false);
                exception(api, 'Ошибка восстановления грузополучателя', ex, () => d(userLoaded(undefined)));
            });
    };
    return (
        <>
            <FormHeader
                title={`${id ? (isViewOnly ? 'Информация о грузополучателе' : 'Изменить грузополучателя') : 'Добавить грузополучателя'}`}
            />
            {!loading && (
                <Form preserve={false} colon={false} labelCol={{ span: 3 }} wrapperCol={{ span: 8 }} form={form} onFinish={onSave}>
                    <Form.Item
                        initialValue={entity?.userId}
                        required
                        label='Клиент'
                        name='userId'
                        rules={[{ required: true, message: 'Укажите учетную запись основного клиента' }]}
                    >
                        <Select
                            disabled={isViewOnly || !!entity.id}
                            onChange={(value) => {
                                setEntity({ ...entity, userId: value });
                                form.setFieldValue('userId', value);
                                form.setFieldValue('code', undefined);
                            }}
                            options={users.map((u) => {
                                return { value: u.id, label: u.login };
                            })}
                        />
                    </Form.Item>
                    <Form.Item
                        initialValue={entity?.code}
                        validateFirst={true}
                        tooltip='Если поле не заполнено, ID сгенерируется атоматически'
                        label='ID'
                        name='code'
                        rules={[
                            {
                                validator: async (_, value: number) => {
                                    const userId = form.getFieldValue('userId');
                                    const isCodeExist = await serverFetch(`consignees/codeexist/${id}/${userId}/${value}`, {
                                        method: 'GET',
                                    })
                                        .then((data) => {
                                            return data;
                                        })
                                        .catch((ex) => {
                                            exception(api, 'Ошибка проверки кода', ex, () => d(userLoaded(undefined)));
                                        });

                                    if (isCodeExist) return Promise.reject('Грузополучатель с указанным кодом уже существует');
                                    return Promise.resolve();
                                },
                            },
                        ]}
                    >
                        <InputNumber
                            disabled={
                                !hasPermission(userSession.permissions, Permission.FullAccess) ||
                                !!entity.id ||
                                !form.getFieldValue('userId')
                            }
                            onChange={(value: number | null) => {
                                setEntity({ ...entity, code: value });
                            }}
                        />
                    </Form.Item>
                    <Form.Item
                        initialValue={entity?.allMarkings}
                        required
                        tooltip="Для разделения маркировок используйте разделитель '/'"
                        label='Маркировка'
                        name='allMarkings'
                        rules={[{ required: true, message: 'Укажите маркировку' }]}
                    >
                        <TextArea
                            autoFocus
                            onInput={(e) => ((e.target as HTMLInputElement).value = (e.target as HTMLInputElement).value.toUpperCase())}
                            disabled={isViewOnly}
                            rows={3}
                            onChange={(data) => {
                                setEntity({ ...entity, allMarkings: data.target.value });
                            }}
                        />
                    </Form.Item>
                    <Form.Item
                        initialValue={entity.cityId}
                        required
                        label='Город доставки'
                        name='city'
                        rules={[{ required: true, message: 'Выберите город доставки' }]}
                    >
                        <Select
                            disabled={isViewOnly}
                            showSearch
                            onChange={(value) => setEntity({ ...entity, cityId: value })}
                            filterOption={(input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase())}
                            filterSort={(a, b) => (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase())}
                            options={cities.map((c) => {
                                return { value: c.id, label: c.name };
                            })}
                        />
                    </Form.Item>
                    <Form.Item initialValue={entity.tag} label='Тег' name='tag'>
                        <Tag
                            initialTags={entity.tags || []}
                            isEditMode={!isViewOnly}
                            api={api}
                            onSelect={(e: Array<string> | undefined) => {
                                setEntity({ ...entity, tags: e });
                            }}
                            serverFetch={serverFetch}
                        />
                    </Form.Item>
                    <Form.Item
                        initialValue={entity.fullName}
                        required
                        label='ФИО'
                        name='fullName'
                        rules={[{ required: true, message: 'Укажите ФИО' }]}
                    >
                        <Input
                            disabled={isViewOnly}
                            onChange={(data) => {
                                setEntity({ ...entity, fullName: data.target.value });
                            }}
                        />
                    </Form.Item>
                    <Form.Item
                        initialValue={entity.address}
                        required
                        label='Адрес'
                        name='address'
                        rules={[{ required: true, message: 'Укажите адрес' }]}
                    >
                        <Input
                            disabled={isViewOnly}
                            onChange={(data) => {
                                setEntity({ ...entity, address: data.target.value });
                            }}
                        />
                    </Form.Item>
                    <PhoneInputFormItem
                        value={entity?.phone}
                        name='phone'
                        label='Телефон'
                        required={true}
                        disabled={isViewOnly}
                        onChange={(value) => {
                            setEntity({ ...entity, phone: value });
                        }}
                    />

                    <Form.Item wrapperCol={{ offset: 3, span: 8 }}>
                        <Space size={'small'} style={{ float: 'right' }}>
                            <Button type='text' onClick={() => navigate(-1)}>
                                Отменить
                            </Button>
                            {!isViewOnly &&
                                (entity.isArchived ? (
                                    (hasPermission(userSession.permissions, Permission.FullAccess) ||
                                        hasPermission(userSession.permissions, Permission.RestoreConsignee)) && (
                                        <Button type='primary' onClick={() => onRestore()}>
                                            Восстановить
                                        </Button>
                                    )
                                ) : (
                                    <Button type='primary' htmlType='submit' loading={loading}>
                                        Сохранить
                                    </Button>
                                ))}
                        </Space>
                    </Form.Item>
                </Form>
            )}

            {contextHolder}
        </>
    );
};

export default Consignee;
